import React, { useState, useRef, useMemo } from 'react';
import classNames from 'classnames';

import Input from 'components/partials/input/input';

import { debounce } from 'helpers/debounce';

import Icons from 'assets/icons';

interface SearchProps {
  setSearch: (value: string) => void;
  customClass?: string;
  placeholder?: string;
  customClassIcon?: string;
  ariaLabel?: string;
}

const Search = ({
  setSearch,
  customClass = '',
  placeholder = 'Search...',
  customClassIcon = '',
  ariaLabel = 'Search',
}: SearchProps): React.JSX.Element => {
  const [searchIsStarted, setSearchIsStarted] = useState(false);

  const ref = useRef<HTMLInputElement>(null);

  const handleOnChange = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
      }, 800),
    []
  );

  return (
    <div
      className={classNames('flex items-center relative', customClass)}
      role="search"
    >
      {searchIsStarted ? (
        <button
          type="button"
          className={classNames(
            'text-navy-400 absolute top-[16px] left-3 bg-transparent border-none p-0 cursor-pointer z-10',
            customClassIcon
          )}
          onClick={() => {
            if (ref.current) {
              ref.current.value = '';
            }
            setSearchIsStarted(false);
            setSearch('');
            ref.current?.focus();
          }}
          aria-label="Clear search"
          data-testid="clear-search-icon"
        >
          <Icons.Plus
            height="100%"
            width="14"
            customClass="rotate-45"
            aria-hidden="true"
          />
        </button>
      ) : (
        <Icons.Search
          customClass={classNames(
            'text-navy-400 left-3 absolute',
            customClassIcon
          )}
          aria-hidden="true"
        />
      )}
      <Input
        ref={ref}
        inputCustomClass="border-navy-400 pl-8 !max-h-11"
        inputWrapperCustomClass="!m-0"
        placeholder={placeholder}
        aria-label={ariaLabel}
        onFocus={(e) => setSearchIsStarted(!!e?.target?.value)}
        onChange={(e) => {
          setSearchIsStarted(!!e?.target?.value);
          handleOnChange(e?.target?.value.trim());
        }}
        data-testid="search-input"
      />
    </div>
  );
};

export default Search;
